/**
 * The admin shell.
 *
 * One gate at the top: until `/admin/me` answers, nothing else mounts. That is
 * a convenience rather than the security boundary — every route behind it is
 * enforced server-side by `requireAdmin`, and this only decides what to draw.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { NavLink, Navigate, Route, Routes } from 'react-router-dom'

import { LoginPage } from './pages/Login'
import { OverviewPage } from './pages/Overview'
import { UsersPage } from './pages/Users'
import { UserDetailPage } from './pages/UserDetail'
import { OpensPage } from './pages/Opens'
import { SettlementPage } from './pages/Settlement'
import { AuditPage } from './pages/Audit'
import { api, ApiError } from './lib/api'

export const meKey = ['admin-me'] as const

interface Me {
  ok: true
  settlement: boolean
}

export function useAdminSession() {
  return useQuery<Me | null>({
    queryKey: meKey,
    queryFn: async () => {
      try {
        return await api.get<Me>('/me')
      } catch (err) {
        // 401 is the normal signed-out answer, not a failure to report.
        if (err instanceof ApiError && err.status === 401) return null
        throw err
      }
    },
    staleTime: 60_000,
    retry: false,
  })
}

const NAV = [
  { to: '/', label: 'Overview', end: true },
  { to: '/users', label: 'Users' },
  { to: '/opens', label: 'Rips' },
  { to: '/settlement', label: 'Settlement' },
  { to: '/audit', label: 'Audit' },
]

export default function App() {
  const { data: me, isLoading } = useAdminSession()

  if (isLoading) {
    return (
      <div className="grid min-h-dvh place-items-center text-sm" style={{ color: 'var(--color-faint)' }}>
        Loading…
      </div>
    )
  }

  if (!me) return <LoginPage />

  return (
    <div className="flex min-h-dvh flex-col">
      <TopBar />
      <main className="mx-auto w-full max-w-[1600px] flex-1 px-5 py-6">
        <Routes>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/users/:id" element={<UserDetailPage />} />
          <Route path="/opens" element={<OpensPage />} />
          <Route path="/settlement" element={<SettlementPage />} />
          <Route path="/audit" element={<AuditPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  )
}

function TopBar() {
  const queryClient = useQueryClient()

  const signOut = async () => {
    await api.post('/logout')
    queryClient.setQueryData(meKey, null)
    queryClient.clear()
  }

  return (
    <header className="sticky top-0 z-40 border-b" style={{ background: 'var(--color-bg)' }}>
      <div className="mx-auto flex w-full max-w-[1600px] items-center gap-6 px-5 py-3">
        <div className="flex items-center gap-2.5">
          <img src="/brand/exlyr-mark-256.png" alt="" width={22} height={22} aria-hidden />
          <span className="text-sm font-bold tracking-tight">Elyxr</span>
          <span
            className="chip"
            style={{ color: 'var(--color-warn)', background: 'color-mix(in srgb, var(--color-warn) 14%, transparent)' }}
          >
            admin
          </span>
        </div>

        <nav className="flex flex-1 items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className="px-3 py-1.5 text-sm font-medium transition-colors"
              style={({ isActive }) => ({
                background: isActive ? 'var(--color-surface-2)' : undefined,
                color: isActive ? 'var(--color-text)' : 'var(--color-muted)',
              })}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button className="btn btn-ghost btn-sm" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </header>
  )
}
